<template>
  <div style="height: 100%;">
    <MglMap
      v-if="loadMap"
      :zoom="10"
      :center="center"
      :access-token="accessToken"
      :interactive="interactive"
      :map-style="mapStyle"
      :track-resize="true"
      @load="onMapLoad"
    >
      <TripsStartMarker
        v-if="start"
        :coordinates="start"
      />
      <TripsStopMarker
        v-if="stop"
        :coordinates="stop"
      />
      <template v-for="(coordinates, index) in stopOvers">
        <TripsStopoverMarker
          :key="index"
          :coordinates="coordinates"
        />
      </template>
      <template v-for="(event, index) in events">
        <trips-event-marker
          :key="index"
          :coordinates="event.location"
          :type="event.type"
        />
      </template>
      <MglGeojsonLayer
        :layer="layer"
        :source="source"
        layer-id="tripLine"
        source-id="tripLine"
      />
    </MglMap>
  </div>
</template>
<script>
import { MglGeojsonLayer, MglMap } from 'vue-mapbox'
import TripsStopMarker from '@/components/trip-detail/maplib/TripsStopMarker'
import TripsStopoverMarker from '@/components/trip-detail/maplib/TripsStopoverMarker'
import TripsStartMarker from '@/components/trip-detail/maplib/TripsStartMarker'
import TripsEventMarker from "@components/trip-detail/maplib/TripsEventMarker";

export default {
  name: 'TripsMap',
  components: {
    TripsEventMarker,
    MglMap,
    MglGeojsonLayer,
    TripsStopMarker,
    TripsStopoverMarker,
    TripsStartMarker,
  },
  props: {
    start: {
      type: Object,
      default: null,
    },
    stop: {
      type: Object,
      default: null,
    },
    stopOvers: {
      type: Array,
      default () {
        return []
      },
    },
    events: {
      type: Array,
      default () {
        return []
      },
    },
    lineCoordinates: {
      type: Array,
      required: true,
    },
    lineColor: {
      type: String,
      default: '#ffdc00',
    },
    lineWidth: {
      type: Number,
      default: 5,
    },
    interactive: {
      type: Boolean,
      default: false,
    },
    accessToken: {
      type: String,
      required: true,
    },
    fullscreen: {
      type: Boolean,
      default: false,
    }
  },
  data () {
    let center = this.start || this.stop
    if (!center && this.stopOvers.length > 0) {
      center = this.stopOvers[0]
    } else if (!center) {
      center = { lat: 48.202455, lng: 16.3208435 }
    }

    return {
      loadMap: !this.lazyLoad,
      map: null,
      center
    }
  },
  computed: {
    source () {
      const coordinates = []
      this.lineCoordinates.forEach((value) => {
        coordinates.push([value.lng, value.lat])
      })
      return {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: coordinates,
          },
        },
      }
    },
    layer () {
      return {
        type: 'line',
        paint: {
          'line-width': this.lineWidth,
          'line-color': this.lineColor,
        },
      }
    },
    mapStyle () {
      return 'mapbox://styles/oeamtcgeo1/cjdzrhx813yla2rqn53x5m7y6'
    },
    boundsPadding () {
      return this.fullscreen ? 100 : 70
    },
    mapBounds () {
      const points = [this.start, this.stop, ...this.stopOvers, ...this.lineCoordinates].filter(p => p)
      let minLat, maxLat, minLng, maxLng
      for (const i in points) {
        const point = points[i]
        if (point.lat > maxLat || !maxLat) {
          maxLat = point.lat
        }
        if (point.lat < minLat || !minLat) {
          minLat = point.lat
        }

        if (point.lng > maxLng || !maxLng) {
          maxLng = point.lng
        }
        if (point.lng < minLng || !minLng) {
          minLng = point.lng
        }
      }
      if (minLat && maxLat && minLng && maxLng) {
        if (minLat === maxLat || minLng === maxLng) {
          const lat = minLat
          const lng = minLng
          minLat = lat - 0.07
          maxLat = lat + 0.07
          minLng = lng - 0.15
          maxLng = lng + 0.15
        }
        return [[minLng, minLat], [maxLng, maxLat]]
      }
      return [[9.47996951665, 46.4318173285], [16.9796667823, 49.0390742051]]
    }
  },
  watch: {
    fullscreen: {
      deep: true,
      handler () {
        this.$nextTick(() => {
          const bounds = this.mapBounds
          if (this.map && bounds) {
            this.map.actions.fitBounds(bounds, { padding: this.boundsPadding, duration: 0 })
          }
        })
      }
    },
    start: {
      deep: true,
      handler () {
        const bounds = this.mapBounds
        if (this.map && bounds) {
          this.map.actions.fitBounds(bounds, { padding: this.boundsPadding })
        }
      },
    },
    stop: {
      deep: true,
      handler () {
        const bounds = this.mapBounds
        if (this.map && bounds) {
          this.map.actions.fitBounds(bounds, { padding: this.boundsPadding })
        }
      },
    },
    stopOvers: {
      deep: true,
      handler () {
        const bounds = this.mapBounds
        if (this.map && bounds) {
          this.map.actions.fitBounds(bounds, { padding: this.boundsPadding })
        }
      },
    },
  },
  mounted () {
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0]) {
        return
      }
      if (entries[0].isIntersecting) {
        this.loadMap = true
      }
    }, {
      rootMargin: '20px',
    })
    observer.observe(this.$el)
  },
  methods: {
    onMapLoad ({ component }) {
      this.map = component
      if (this.mapBounds) {
        this.map.actions.fitBounds(this.mapBounds, { padding: this.boundsPadding, duration: 0 })
      }
    }
  },
}
</script>
